import {
  AccountScreen, AppShell, Button, EmptyState, entryRoutes, NotificationCentre, PageHeader, PasskeysCard,
  Permissions, RequirePermission, RequireSession, Row, Stack, useI18n, useSession, type NavItem,
} from '@daghfal/core';
import { Route, Routes } from 'react-router-dom';
import { Accounts } from './routes/Accounts';
import { Allergens } from './routes/Allergens';
import { Announcements } from './routes/Announcements';
import { Audit } from './routes/Audit';
import { Backups } from './routes/Backups';
import { Dashboard } from './routes/Dashboard';
import { NewTenant } from './routes/NewTenant';
import { PlatformUsers } from './routes/PlatformUsers';
import { TenantDetail } from './routes/TenantDetail';
import { Tenants } from './routes/Tenants';

export function App() {
  const { t } = useI18n();

  const brand = { name: t('console.name'), tagline: t('console.tagline') };

  return (
    <Routes>

      {entryRoutes(brand)}
      <Route path="/*" element={<RequireSession><ConsoleShell /></RequireSession>} />
    </Routes>
  );
}

function ConsoleShell() {
  const { t } = useI18n();
  const { session, signOut } = useSession();

  const nav: NavItem[] = [
    { to: '/', label: t('nav.dashboard'), icon: '📊', end: true },
    { to: '/tenants', label: t('nav.tenants'), icon: '🏫', permission: Permissions.platform.viewTenants },
    { to: '/users', label: t('nav.users'), icon: '👤', permission: Permissions.platform.manageUsers },
    { to: '/accounts', label: t('nav.accounts'), icon: '🔑', permission: Permissions.platform.recoverIdentities },
    { to: '/announcements', label: t('nav.announcements'), icon: '📣', permission: Permissions.platform.sendAnnouncements },
    { to: '/audit', label: t('nav.audit'), icon: '🧾', permission: Permissions.platform.viewAudit },
    { to: '/backups', label: t('nav.backups'), icon: '🗄️', permission: Permissions.platform.manageBackups },
    { to: '/allergens', label: t('nav.allergens'), icon: '🥜', permission: Permissions.platform.manageAllergens },
  ];

  // A tenant-scoped user who reaches this host has signed in successfully and holds no platform
  // permission at all. Saying so plainly is better than an empty console that looks broken.
  if (session?.membership.scope !== 'Platform') {
    return (
      <AppShell brand={t('console.name')} nav={[]}>
        <EmptyState icon="⛔" title={t('error.forbidden')} tone="warning" />
      </AppShell>
    );
  }

  // A password session reaches no platform permission (Q25). Until it adds a passkey and signs in with it, the
  // one thing it can do here is add one.
  if (session.passkeyRequired) {
    return (
      <AppShell brand={t('console.name')} nav={[]}>
        <Stack>
          <PageHeader title={t('passkeyRequired.title')} lede={t('passkeyRequired.body')} />
          <PasskeysCard />
          <Row><Button variant="secondary" onClick={() => void signOut()}>{t('passkeyRequired.signOut')}</Button></Row>
        </Stack>
      </AppShell>
    );
  }

  return (
    <AppShell
      brand={t('console.name')} context={session.membership.roleNameAr} nav={nav}
      notificationsPath="/notifications"
    >
      <Routes>
        <Route index element={<Dashboard />} />
        <Route
          path="tenants"
          element={<RequirePermission all={[Permissions.platform.viewTenants]}><Tenants /></RequirePermission>}
        />
        <Route
          path="tenants/new"
          element={<RequirePermission all={[Permissions.platform.createTenants]}><NewTenant /></RequirePermission>}
        />
        <Route
          path="tenants/:id"
          element={<RequirePermission all={[Permissions.platform.viewTenants]}><TenantDetail /></RequirePermission>}
        />
        <Route
          path="users"
          element={<RequirePermission all={[Permissions.platform.manageUsers]}><PlatformUsers /></RequirePermission>}
        />
        <Route
          path="accounts"
          element={<RequirePermission all={[Permissions.platform.recoverIdentities]}><Accounts /></RequirePermission>}
        />
        <Route
          path="announcements"
          element={
            <RequirePermission all={[Permissions.platform.sendAnnouncements]}>
              <Announcements />
            </RequirePermission>
          }
        />
        <Route
          path="audit"
          element={<RequirePermission all={[Permissions.platform.viewAudit]}><Audit /></RequirePermission>}
        />
        <Route
          path="backups"
          element={<RequirePermission all={[Permissions.platform.manageBackups]}><Backups /></RequirePermission>}
        />
        <Route
          path="allergens"
          element={<RequirePermission all={[Permissions.platform.manageAllergens]}><Allergens /></RequirePermission>}
        />
        {/* The inbox only. No notification channels: preferences belong to a tenant membership, and
            platform staff hold none (decision 2026-09-23 #15). */}
        <Route path="notifications" element={<NotificationCentre showPreferences={false} />} />
        <Route path="account" element={<AccountScreen />} />
        <Route path="*" element={<EmptyState icon="🔍" title={t('error.notFound')} />} />
      </Routes>
    </AppShell>
  );
}
